import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import {
  COMPONENT_SIZE_LARGE,
  COMPONENT_SIZE_MEDIUM,
  COMPONENT_SIZE_SMALL,
  ComponentSize,
} from '../../../typings/design-tokens';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import IconArrowDown from '../../atoms/icons/icon-arrow-down';
import IconCalendar from '../../atoms/icons/icon-calendar';
import MenuItem from '../../atoms/menu-item/menu-item';
import './menu-list.scss';

// Use generaic design tokens
export const menuSizeList: ComponentSize[] = [COMPONENT_SIZE_SMALL, COMPONENT_SIZE_MEDIUM, COMPONENT_SIZE_LARGE];
export const menuVariantList: MenuVariant[] = ['DROPDOWN', 'CALENDAR'];

export interface IMenuListLink {
  label: string;
  href?: string;
  isSelected?: boolean;
}

type MenuVariant = 'DROPDOWN' | 'CALENDAR';

export interface IMenuList {
  links: Array<IMenuListLink>;
  text: string;
  title?: string;
  showSelected?: boolean;
  menuSize?: ComponentSize;
  onClick?: (event: React.MouseEvent, id: number) => any;
  useJS?: boolean;
  variant?: MenuVariant;
}

const classNames = (menuSize: string, isMenuVisible: boolean, useJS: boolean, variant: MenuVariant): IClassNames =>
  makeClassNames({
    menuList: [
      useJS ? (isMenuVisible ? 'menu-list--opened' : 'menu-list--closed') : 'menu-list', // eslint-disable-line no-nested-ternary
      menuSize ? `menu-list--${menuSize}` : '',
    ],
    menuButton: [variant === 'CALENDAR' ? 'menu-list__button--calendar' : 'menu-list__button'],
    menuTitle: 'menu-list__title',
    menuButtonTitle: 'menu-list__button__p',
    menuButtonIcon: 'menu-list__button__arrow',
    menuDropdown: [variant === 'CALENDAR' ? ['menu-list__menu--calendar', 'menu-list__menu'] : 'menu-list__menu'],
  });

const MenuList: React.FC<IMenuList> = ({
  /* istanbul ignore next: default value on mandatory field */ links = [],
  /* istanbul ignore next: default value on mandatory field */ onClick = () => {},
  /* istanbul ignore next: default value on mandatory field */ showSelected = false,
  /* istanbul ignore next: default value on mandatory field */ menuSize = COMPONENT_SIZE_SMALL,
  /* istanbul ignore next: default value on mandatory field */ useJS = false,
  /* istanbul ignore next: default value on mandatory field */ variant = 'DROPDOWN',
  text,
  title,
}) => {
  // INTERNAL STATE for MENU SHOW/HIDE
  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const [isOnMouseEnterLastEvent, setIsOnMouseEnterLastEvent] = useState<boolean>(false);
  const toggleMenuVisible = (forcedMenuVisible?: boolean) =>
    useJS && setMenuVisible(forcedMenuVisible ?? !isMenuVisible);
  const onMenuItemClick = (event: React.MouseEvent, selectedIndex: number) => {
    toggleMenuVisible();
    onClick(event, selectedIndex);
  };
  // Uncomment to DEBUG menu visibility
  // useEffect(() => {
  //   console.log('isMenuVisible', { isMenuVisible });
  // }, [isMenuVisible]);

  const cn = useMemo(
    () => classNames(menuSize, isMenuVisible, useJS, variant),
    [menuSize, isMenuVisible, useJS, variant]
  );

  return (
    <div
      className={cn.menuList}
      onMouseEnter={() => {
        setIsOnMouseEnterLastEvent(true);
        toggleMenuVisible(true);
      }}
      onMouseLeave={() => {
        setIsOnMouseEnterLastEvent(false);
        toggleMenuVisible(false);
      }}
    >
      <div
        onClick={() => {
          if (!isOnMouseEnterLastEvent) {
            toggleMenuVisible();
          }
          setIsOnMouseEnterLastEvent(false);
        }}
      >
        {variant === 'DROPDOWN' && title && <p className={cn.menuTitle}>{title}</p>}
        <button type="button" className={cn.menuButton}>
          <span className={cn.menuButtonTitle}>{text}</span>
          <div className={cn.menuButtonIcon} aria-hidden>
            {variant === 'CALENDAR' ? (
              <IconCalendar size="xsmall" color="dark" verticalAlign="middle" />
            ) : (
              <IconArrowDown size="xxxsmall" color="blue" />
            )}
          </div>
        </button>
      </div>
      <div className={cn.menuDropdown} role="listbox">
        {links.map((link: IMenuListLink, index: number) => {
          if (!link.isSelected || (link.isSelected && showSelected)) {
            return (
              <MenuItem
                callback={onMenuItemClick}
                href={link && link.href}
                id={index}
                isSelected={link.isSelected}
                key={link.label + link.href}
                label={link.label}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MenuList.propTypes = {
    links: PropTypes.arrayOf(
      PropTypes.shape({
        href: isPropTypeNullable(PropTypes.string),
        isSelected: isPropTypeNullable(PropTypes.bool),
        label: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    menuSize: PropTypes.oneOf(menuSizeList),
    onClick: PropTypes.func,
    showSelected: PropTypes.bool,
    text: PropTypes.string.isRequired,
    title: PropTypes.string,
    variant: isPropTypeNullable(PropTypes.string),
  };
}

export default MenuList;
